// Gate optionality matrix — Gate G3.7 Phase 0.
//
// Source: ``plan/AI_ALM_Gate_G3_7_Implementation_Plan.md`` §2.2.4.
//
// Splits gates into two camps:
//
//   * **Mandatory (no Skip button):** G1, G4, G5 — the workflow can't
//     function without them. Every project must answer them.
//   * **Optional (Skip button visible):** G2, G2.5, G3, G3.5, G3.7 —
//     visibility already depends on intervention budget + project
//     shape, so the architect can additionally opt out of an emitted
//     optional gate by recording an explicit ``skip_reason``. The
//     SRD Technical Depth Audit (G3.7) is auto-skipped at ``minimal``
//     budget by the visibility matrix; at ``standard`` / ``thorough``
//     the architect can still skip-with-reason if findings are out
//     of scope.
//
// The ``SkipGate`` panel reads this table to decide whether to render
// its "Skip with reason …" affordance. The constant is frontend-only
// because the backend gate enforcer already accepts ``skipped``
// status with ``skip_reason`` on every gate; the FE simply chooses
// which gates are allowed to receive the skip submission. Mandatory
// gates have no Skip button — there is no UX path that produces a
// skipped decision for them.
//
// When adding a new gate, decide its optionality here. The hidden
// hazard: if you mark a gate Optional but the cascade in
// ``_PHASE_TO_PRIOR_GATES`` requires it for a downstream phase, the
// architect skipping the gate will still unblock that phase
// (skipped == resolved per §2.3). That's the contract — Optional
// gates can be intentionally elided.

import type { FocusKey } from './focusKeys'

/** Whether a gate must be answered (mandatory) or can be skipped (optional). */
export type GateOptionality = 'mandatory' | 'optional'

/**
 * Gate optionality matrix per the implementation plan §2.2.4.
 *
 * Keys are the focus keys used by ``CommandCenterPage`` so the
 * caller doesn't have to translate gate numbers ↔ focus keys.
 */
export const GATE_OPTIONALITY: Record<FocusKey, GateOptionality | null> = {
  // Briefing isn't a gate — it has its own approval flow.
  briefing: null,
  // Mandatory gates — every workflow must clear these.
  gate1: 'mandatory',
  gate4: 'mandatory',
  gate5: 'mandatory',
  // Optional gates — already filtered by the visibility matrix
  // (intervention budget, compliance cues), but additionally
  // skippable with a recorded reason at the architect's discretion.
  gate2: 'optional',
  gate25: 'optional',
  gate3: 'optional',
  gate35: 'optional',
  // Gate G3.7 (SRD Technical Depth Audit) — Phase 6 wiring per
  // plan ``AI_ALM_Gate_G3_7_Implementation_Plan.md`` §8. Optional
  // for the same reason G3 / G3.5 are: the architect may decide
  // depth findings are out-of-scope for this run.
  gate37: 'optional',
  // Pipeline / file / export surfaces — no gate, no Skip affordance.
  pipeline2: null,
  pipeline3: null,
  pipeline4: null,
  pipeline5: null,
  artifacts: null,
  inputs: null,
  export: null,
  // Approval Chain is an intake focus surface, not a gate.
  approvalChain: null,
  // AI Governance Ratification (2026-05-28) is a Pre-Export Review
  // surface — not a gate in the Skip/Approve sense; the architect
  // ratifies via the card, no optional/mandatory toggle applies.
  aiGovernance: null,
}

/**
 * True when ``focusKey`` represents a gate the architect may
 * legitimately skip with a recorded reason. Mandatory gates and
 * non-gate surfaces (pipelines, briefing, export) return ``false``.
 */
export function isOptionalGate(focusKey: FocusKey): boolean {
  return GATE_OPTIONALITY[focusKey] === 'optional'
}

/**
 * True when ``focusKey`` is a gate the workflow refuses to skip.
 * The SkipGate panel must NOT render for these.
 */
export function isMandatoryGate(focusKey: FocusKey): boolean {
  return GATE_OPTIONALITY[focusKey] === 'mandatory'
}

/**
 * Maximum length (in characters) of the architect's skip
 * justification per the plan §2.2.3 dialog spec.
 */
export const MAX_SKIP_REASON_LENGTH = 500
